import { useState } from 'react';

import { useAuth } from '../providers/auth-provider';

import { Button } from './Button';
import { Input } from './Input';

export interface LoginScreenProps {
  externalEmail?: string | null | undefined;
}

export function LoginScreen({ externalEmail }: LoginScreenProps = {}) {
  const { login, verifyCode, error } = useAuth();
  const isMfaMode = !!externalEmail;
  const [step, setStep] = useState<'email' | 'code'>(isMfaMode ? 'email' : 'email');
  const [email, setEmail] = useState(externalEmail ?? '');
  const [code, setCode] = useState('');
  const [isEmailSubmitting, setIsEmailSubmitting] = useState(false);
  const [isCodeSubmitting, setIsCodeSubmitting] = useState(false);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsEmailSubmitting(true);
    try {
      await login(isMfaMode ? externalEmail! : email);
      setIsEmailSubmitting(false);
      setStep('code');
    } catch {
      setIsEmailSubmitting(false);
    }
  };

  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsCodeSubmitting(true);
    try {
      await verifyCode(isMfaMode ? externalEmail! : email, code);
    } catch {
      // error displayed via useAuth().error
    } finally {
      setIsCodeSubmitting(false);
    }
  };

  const handleResendCode = async () => {
    setIsEmailSubmitting(true);
    try {
      await login(isMfaMode ? externalEmail! : email);
    } catch {
      // error displayed
    } finally {
      setIsEmailSubmitting(false);
    }
  };

  return (
    <div className="sd-login-grid sd-login-ambient flex min-h-screen items-center justify-center bg-sd-background relative overflow-hidden">
      <div
        data-testid="login-card"
        className="sd-reveal-1 relative z-10 w-full max-w-sm rounded-sd-lg bg-sd-surface p-sd-8 shadow-[0_25px_50px_-12px_rgba(0,0,0,0.15),0_8px_20px_-4px_rgba(0,0,0,0.08),0_0_0_1px_rgba(0,0,0,0.03)] border border-sd-border/20 backdrop-blur-sm transition-all duration-300"
      >
        <div className="absolute top-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-sd-warning/50 to-transparent" />

        <h1 className="sd-reveal-2 mb-sd-1 text-center text-[28px] tracking-[0.15em] text-sd-text">
          <span className="font-light">shift</span>
          <span className="font-bold">agent</span>
        </h1>
        {isMfaMode ? (
          <p className="sd-reveal-3 mb-sd-2 text-center text-sd-xs font-medium text-sd-text-muted tracking-[0.3em] uppercase">
            Identity Verification
          </p>
        ) : (
          <p className="sd-reveal-3 mb-sd-2 text-center text-sd-xs font-medium text-sd-text-muted tracking-[0.3em] uppercase">
            Command Center
          </p>
        )}
        <div className="sd-reveal-3 mx-auto mb-sd-6 h-px w-16 bg-gradient-to-r from-transparent via-sd-warning/60 to-transparent" />

        <div className="sd-reveal-4 relative">
          {/* MFA Mode: Email step shows locked email + send code button */}
          {isMfaMode && step === 'email' && (
            <div data-testid="login-step-mfa">
              <p className="mb-sd-3 text-sd-sm text-sd-text-secondary text-center">
                We need to verify your identity. A verification code will be sent to:
              </p>
              <div className="mb-sd-4 px-sd-3 py-sd-2 bg-sd-background/50 border border-sd-border/30 rounded-sd-md text-center">
                <span className="text-sd-base font-medium text-sd-text">{externalEmail}</span>
              </div>
              {isEmailSubmitting && (
                <div className="mb-sd-3 animate-[sd-fade-in_200ms_ease-out]" role="status">
                  <div className="relative overflow-hidden rounded-sd-sm bg-sd-warning/5 border border-sd-warning/15 px-sd-3 py-sd-2">
                    <p className="text-sd-xs font-medium tracking-wide text-sd-warning">
                      Sending verification code…
                    </p>
                    <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
                      <div className="absolute inset-y-0 w-1/2 bg-gradient-to-r from-transparent via-sd-warning/8 to-transparent animate-[sd-shimmer-sweep_1.8s_ease-in-out_infinite]" />
                    </div>
                  </div>
                </div>
              )}
              {error && !isEmailSubmitting && (
                <p className="mb-sd-3 text-sd-sm text-sd-error shadow-[0_0_0_1px] shadow-sd-error/20 rounded-sd-sm px-sd-2 py-sd-1 bg-sd-error/5">
                  {error}
                </p>
              )}
              <Button
                variant="primary"
                loading={isEmailSubmitting}
                className="w-full font-semibold"
                onClick={() => { void handleEmailSubmit(new Event('click') as unknown as React.FormEvent); }}
              >
                Send Verification Code
              </Button>
            </div>
          )}

          {/* Standard Mode: Email input */}
          {!isMfaMode && (
            <div
              data-testid="login-step-email"
              className={`transition-opacity duration-300 ${step === 'email' ? 'opacity-100' : 'opacity-0 absolute inset-0 pointer-events-none'}`}
            >
              <form onSubmit={handleEmailSubmit} aria-label="Email login form">
                <label htmlFor="email" className="mb-sd-1 block text-sd-sm font-medium text-sd-text-secondary tracking-wide">
                  Email address
                </label>
                <Input
                  id="email"
                  variant="text"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isEmailSubmitting}
                  className="bg-sd-background/50"
                />
                {isEmailSubmitting && (
                  <div className="mt-sd-3 animate-[sd-fade-in_200ms_ease-out]" role="status">
                    <div className="relative overflow-hidden rounded-sd-sm bg-sd-warning/5 border border-sd-warning/15 px-sd-3 py-sd-2">
                      <p className="text-sd-xs font-medium tracking-wide text-sd-warning">
                        Sending verification code…
                      </p>
                      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
                        <div className="absolute inset-y-0 w-1/2 bg-gradient-to-r from-transparent via-sd-warning/8 to-transparent animate-[sd-shimmer-sweep_1.8s_ease-in-out_infinite]" />
                      </div>
                    </div>
                  </div>
                )}
                {error && step === 'email' && !isEmailSubmitting && (
                  <p className="mt-sd-2 text-sd-sm text-sd-error shadow-[0_0_0_1px] shadow-sd-error/20 rounded-sd-sm px-sd-2 py-sd-1 bg-sd-error/5">
                    {error}
                  </p>
                )}
                <div className="mt-sd-6">
                  <Button type="submit" variant="primary" loading={isEmailSubmitting} className="w-full font-semibold">
                    Continue
                  </Button>
                </div>
              </form>
            </div>
          )}

          {/* Code verification step (shared by both modes) */}
          <div
            data-testid="login-step-code"
            className={`transition-opacity duration-300 ${step === 'code' ? 'opacity-100' : 'opacity-0 absolute inset-0 pointer-events-none'}`}
          >
            <form onSubmit={handleCodeSubmit} aria-label="Code verification form">
              {isMfaMode && (
                <p className="mb-sd-3 text-sd-sm text-sd-text-secondary text-center">
                  Enter the code sent to <strong className="text-sd-text">{externalEmail}</strong>
                </p>
              )}
              <label htmlFor="code" className="mb-sd-1 block text-sd-sm font-medium text-sd-text-secondary tracking-wide">
                Verification code
              </label>
              <Input
                id="code"
                variant="text"
                placeholder="Enter 6-digit code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                disabled={isCodeSubmitting}
                className="bg-sd-background/50"
              />
              {isCodeSubmitting && (
                <div className="mt-sd-3 animate-[sd-fade-in_200ms_ease-out]" role="status">
                  <div className="relative overflow-hidden rounded-sd-sm bg-sd-warning/5 border border-sd-warning/15 px-sd-3 py-sd-2">
                    <p className="text-sd-xs font-medium tracking-wide text-sd-warning">
                      Verifying code…
                    </p>
                    <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
                      <div className="absolute inset-y-0 w-1/2 bg-gradient-to-r from-transparent via-sd-warning/8 to-transparent animate-[sd-shimmer-sweep_1.8s_ease-in-out_infinite]" />
                    </div>
                  </div>
                </div>
              )}
              {error && step === 'code' && !isCodeSubmitting && (
                <p className="mt-sd-2 text-sd-sm text-sd-error shadow-[0_0_0_1px] shadow-sd-error/20 rounded-sd-sm px-sd-2 py-sd-1 bg-sd-error/5">
                  {error}
                </p>
              )}
              <div className="mt-sd-6">
                <Button type="submit" variant="primary" loading={isCodeSubmitting} className="w-full font-semibold">
                  Verify
                </Button>
              </div>
              <div className="mt-sd-3 text-center flex gap-sd-3 justify-center">
                <button
                  type="button"
                  className="text-sd-sm text-sd-text-secondary underline hover:text-sd-text transition-colors duration-200"
                  onClick={() => { void handleResendCode(); }}
                  disabled={isEmailSubmitting}
                >
                  Resend code
                </button>
                {!isMfaMode && (
                  <button
                    type="button"
                    className="text-sd-sm text-sd-text-secondary underline hover:text-sd-text transition-colors duration-200"
                    onClick={() => setStep('email')}
                  >
                    Back
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>

      <p className="absolute bottom-6 left-0 right-0 text-center text-sd-xs text-sd-text-muted/40 tracking-[0.15em]">
        Secure zero-trust authentication
      </p>
    </div>
  );
}
